import { resolve } from "aurelia";
import { IRouter } from "@aurelia/router";
import { IStore } from "@aurelia/state";

import axios, { AxiosError, AxiosResponse } from "axios";
import _ from "lodash";
import { Spacetime } from "spacetime";

import { wait } from "@/components/shared/tables/typedFakeData";
import {
  airConditioningChecklist,
  auxUPSChecklist,
  closestApproachChecklist,
  collisionChecklist,
  compressedAirChecklist,
  dpConsequenceAnalysisChecklist,
  dpNetworkChecklist,
  dpPositionReferenceChecklist,
  dpThrusterControllersChecklist,
  dpVentilationChecklist,
  dpWorkstationsChecklist,
  draftChecklist,
  driveOffDriftOffChecklist,
  essentialCommsChecklist,
  fireChecklist,
  floodChecklist,
  gangwayEnvLimitChecklist,
  prevailingWeatherForceChecklist,
  thrusterGen24VdcChecklist,
  weatherForecastChecklist,
} from "@/helpers/staticData";
import { getApiUrl } from "@/helpers/util";
import {
  APIDataGroup,
  APIDataPoint,
  APIDataPointDesc,
  ECobrasJobStatus,
  EQueryFlow,
  IAckRequestData,
  IAckResponseData,
  IAlarmDataResponse,
  IAlarmHistoryAPIResponse,
  IAlarmRequestParams,
  IAlarmSystemData,
  IBackofficeDataPointConfig,
  IChartElement,
  ICobrasJob,
  ICobrasJobConfig,
  ICobrasJobResult,
  ICompany,
  ICompanyAccess,
  ICompanyAccessRequestData,
  ICompanyProfile,
  ICompanyUser,
  IDashboardCard,
  IDashboardCardResponse,
  IDashboardConfigListItem,
  IDataSystem,
  IEventHistoryRequestBody,
  IEventLogResponse,
  IFilter,
  IFuelDataResponse,
  IGetDataResponse,
  IGetDataVesselData,
  IKeyGenResponse,
  IKeyStatusResponse,
  INodeConnection,
  INoteCreateParams,
  INoteFetchParams,
  INoteResponseData,
  IReportData,
  IReportRequestBody,
  ISystemDataCountResponse,
  ISystemFunction,
  ISystemModule,
  IUserAccess,
  IUserAccessRequestData,
  IUserSettingsUpdateRequestParams,
  IVesselAlarmSystemListAPIResponse,
  IVesselDatapointsListItem,
  IVesselInfoPageData,
  IVesselRowData,
  IVesselState,
  IVesselStateRequestBody,
} from "@/interfaces";
import { IAlarmCondition, LockValue } from "@/routes/alarms/alarm-conditions/mt-alarm-rules";
import { IAsogItem } from "@/routes/easog/mt-easog";
import { IAltVessel, IVessel } from "@/shared/user";
import { LogoutAction } from "@/static/storeActions";
import { IState } from "@/store/state";

import { IStreamConfig } from "./stream";

export class Api {
  // private controllers: Record<string, any> = {};

  private readonly store = resolve(IStore) as IStore<IState, LogoutAction>;
  private readonly router = resolve(IRouter) as IRouter;

  constructor() {
    axios.defaults.baseURL = getApiUrl("/v1");
    axios.defaults.withCredentials = true;

    // Add a request interceptor
    axios.interceptors.request.use(
      (config) => {
        // Attach header to every request
        config.headers["X-Retarded-DB-Path"] = window.location.pathname;
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add a response interceptor
    axios.interceptors.response.use(null, (res) => {
      if (res?.response?.status === 401) {
        this.store.dispatch({ type: "logout" });
        let url = "login";
        // If there is an active navigation AND it is not already in login page, navigate to login with current page being the returnTo
        if (this.router.activeNavigation && !this.router.activeNavigation.path.includes("login"))
          url += `?returnTo=${this.router.activeNavigation.path}`;
        this.router.load(url);
      }
      return Promise.reject(res);
    });
  }

  // Auth endpoints
  async login(userName: string, passWord: string) {
    const request = axios.post(`login`, {
      userName,
      passWord,
    });

    return request;
  }

  async register(username: string, password: string) {
    const request = axios.post(`register`, {
      username,
      password,
    });

    return request;
  }

  async logout() {
    const request = axios.post(`logout`);
    return request;
  }

  async updatePassword(oldPassword: string, newPassword: string) {
    const request = axios.post(`password`, {
      old: oldPassword,
      new: newPassword,
      cnf: newPassword,
    });
    return request;
  }

  async getCamList() {
    const request = axios.post(`cam-list`);

    return request;
  }

  async getCamStatusList(cameraIds: string[]) {
    const request = axios.post(`cam-status`, cameraIds, {
      headers: {
        "Content-Type": "application/json",
      },
    });

    return request;
  }

  async getFuelData(vesselId: string, from: Date, to: Date) {
    const requestBody = {
      vesselId: vesselId,
      timeFrom: from, // "2022-01-01T00:00:00.00",
      timeTo: to, //"2022-01-5T00:00:00.00",
    };

    const request = axios.post<IFuelDataResponse>(`fuel-data`, requestBody);
    return request;
  }

  async getTrendData(
    tags: number[],
    vesselIds: string[],
    from: Date | string,
    to: Date | string,
    configuration: {
      // alarmTreshold: boolean; // If true, first element
      dpDescription?: boolean; // description data for all data points
      timeInterval?: number; // Number of seconds between each data point
      res?: number; // Total number of data points in response
      alarmThreshold?: boolean;
      aggDataOut?: boolean;
      sourceDataOut?: boolean;
      dataPointInfo?: boolean; // Source data info
    }
  ) {
    const vesselsAndPointsArray = vesselIds.map((vessel) => ({
      dPoints: tags,
      dbId: vessel,
    }));

    const requestBody = {
      res: 100,
      alarmThreshold: true,
      dataPointInfo: false,
      timeFrom: from, // "2022-01-01T00:00:00.00",
      timeTo: to, //"2022-01-5T00:00:00.00",
      filter: vesselsAndPointsArray,
      ...configuration,
    };

    const request = axios.post<IGetDataResponse>(`get-data`, [requestBody]);
    return request;
  }

  // Custom trends endpoints
  getVesselList(): IVessel[] {
    return this.store.getState().user.assets.vessels;
  }

  getFullVesselList(): Promise<AxiosResponse<IAltVessel[]>> {
    const request = axios.post<IAltVessel[]>(`vessel-list`);
    return request;
  }

  async getFormSubmission(formId: string, submissionId: string): Promise<IAsogItem[]> {
    return wait<IAsogItem[]>(
      2000,

      [
        {
          itemCategory: "Weather Conditions and Vessel Performance",
          id: "Weather Forecast",
          itemName: "Weather Forecast",
          greenDescription: "Under 44kts and 2.5m Seas (Hs) / Max (Hmax) 4m Seas or less",
          blueDescription: "Approaching operating limits",
          yellowDescription: "",
          redDescription: "",
          type: "checkbox",
          checklist: weatherForecastChecklist,
        },
        {
          itemCategory: "Weather Conditions and Vessel Performance",
          id: "Prevailing weather force",
          itemName: "Prevailing weather force",
          greenDescription: "Not towards asset (Preferred)",
          blueDescription: "Towards asset Risk Assessment in Place",
          yellowDescription: "",
          redDescription: "",
          type: "checkbox",
          checklist: prevailingWeatherForceChecklist,
        },
        {
          itemCategory: "Weather Conditions and Vessel Performance",
          id: "DRIVE OFF / DRIFT OFF",
          itemName: "DRIVE OFF / DRIFT OFF",
          greenDescription: "Thruster loading as expected No discrepancies observed in PRS's",
          blueDescription:
            "Inexplicable ramp up of thrusters observed.-Discrepancies observed in PRS's:",
          yellowDescription: "Immediately when recognized by DPO",
          redDescription: "Unable to bring vessel under control",
          type: "checkbox",
          checklist: driveOffDriftOffChecklist,
        },
        {
          itemCategory: "Weather Conditions and Vessel Performance",
          id: "W2W Gangway Environmental Limitations per CAMO",
          itemName: "W2W Gangway Environmental Limitations per CAMO",
          greenDescription: "Within specified environmental lmits and parameters",
          blueDescription: "Appraoching environmental lmits and parameters",
          yellowDescription: "Environmental lmits and parameters exceeded",
          redDescription: "",
          type: "checkbox",
          checklist: gangwayEnvLimitChecklist,
        },
        // {
        //   itemCategory: "Weather Conditions and Vessel Performance",
        //   itemName: "Vessel Footprint / Weather Related Position Excursion",
        //   greenDescription: "No position alarms or warnings",
        //   blueDescription:
        //     "Frequent position warnings or first position excursion.  Move to a safer working position or stop the job",
        //   yellowDescription: "Position excursions causing position alarms  (Exceeding 3m)",
        //   redDescription: "",
        //   type: "",
        //   greenCondition: "",
        //   blueCondition: "",
        //   yellowCondition: "",
        //   redCondition: "",
        // },
        // {
        //   itemCategory: "Weather Conditions and Vessel Performance",
        //   itemName: "Heading excursion",
        //   greenDescription: "No heading alarms or warning",
        //   blueDescription:
        //     "Frequent heading warnings or first heading excursion.  Move to a safer working position or Stop the Job",
        //   yellowDescription: "Heading excursions triggering heading alarms (Exceeding 3 degrees)",
        //   redDescription: "",
        //   type: "",
        //   greenCondition: "",
        //   blueCondition: "",
        //   yellowCondition: "",
        //   redCondition: "",
        // },
        {
          itemCategory: "Weather Conditions and Vessel Performance",
          id: "Draft",
          itemName: "Draft",
          greenDescription: "Props remaining below surface",
          blueDescription: "Props surfacing or aerating",
          yellowDescription:
            "Props surfacing or aerating and affecting vessel station keeping capabilities / performance",
          redDescription: "",
          type: "checkbox",
          checklist: draftChecklist,
        },
        {
          itemCategory: "Weather Conditions and Vessel Performance",
          id: "Closest Approach and Separation Limit",
          itemName: "Closest Approach and Separation Limit",
          greenDescription: "More than 10m separation maintained at all times",
          blueDescription: "Separation approaching 10m",
          yellowDescription: "Within 10m requires MOC",
          redDescription: "",
          type: "checkbox",
          checklist: closestApproachChecklist,
        },
        // {
        //   itemCategory: "Thrusters Main Propulsion and Steering",
        //   itemName:
        //     "All main propulsion  & bow tunnel thrusters and stern tunnel thrusters and rudders",
        //   greenDescription: "Operational with no alarms",
        //   blueDescription: "Any alarm, poor performance, unexpected or unexplained event",
        //   yellowDescription: "Loss of either main propulsion, thruster",
        //   redDescription: "",
        //   type: "",
        //   greenCondition: "",
        //   blueCondition: "",
        //   yellowCondition: "",
        //   redCondition: "",
        // },
        {
          id: "rule-1",
          itemCategory: "Thrusters Main Propulsion and Steering",
          itemName: "BTT1 Force Limits",
          greenDescription: "Less than 45% force (562kW)",
          blueDescription: "Approaching 45% continuous force (562kW)",
          yellowDescription: "Exceeding 45% continuous force (562kW)",
          redDescription: "",
          type: "formula",
          datapoints: [
            {
              info: {
                cat: "Thruster 1",
                seq: 314,
                tag: "NULL",
                unit: "tbd",
                label: "Power Feedback",
                dataSystemName: "DPS",
                metricId: 960,
              },
              alias: "load",
            },
          ],
        },
        {
          id: "rule-2",
          itemCategory: "Thrusters Main Propulsion and Steering",
          itemName: "BTT2 Force Limits",
          greenDescription: "Less than 45% force (562kW)",
          blueDescription: "Approaching 45% continuous force (562kW)",
          yellowDescription: "Exceeding 45% continuous force (562kW)",
          redDescription: "",
          type: "formula",
          datapoints: [
            {
              info: {
                cat: "Thruster 2",
                seq: 315,
                tag: "NULL",
                unit: "tbd",
                label: "Power Feedback",
                dataSystemName: "DPS",
                metricId: 961,
              },
              alias: "load",
            },
          ],
        },
        {
          id: "rule-3",
          itemCategory: "Thrusters Main Propulsion and Steering",
          itemName: "BTT3 Force Limits",
          greenDescription: "Less than 45% force (562kW)",
          blueDescription: "Approaching 45% continuous force (562kW)",
          yellowDescription: "Exceeding 45% continuous force (562kW)",
          redDescription: "",
          type: "formula",
          datapoints: [
            {
              info: {
                cat: "Thruster 3",
                seq: 316,
                tag: "NULL",
                unit: "tbd",
                label: "Power Feedback",
                dataSystemName: "DPS",
                metricId: 962,
              },
              alias: "load",
            },
          ],
        },
        {
          itemCategory: "Thrusters Main Propulsion and Steering",
          id: "Main Propulsion Force Limits",
          itemName: "Main Propulsion Force Limits",
          greenDescription: "Less than 45% (832kW) force on both",
          blueDescription: "Approaching 45% (832kW) continuous force on either",
          yellowDescription: "Exceeding 45% (832kW) continuous force on either",
          redDescription: "",
          type: "formula",
          datapoints: [],
        },
        // {
        //   itemCategory: "Diesel Generators",
        //   itemName: "480 Vac Switchboards (LVS1, LVS2 and ELVS)",
        //   greenDescription: "All open-480V SWB (LVS1 & LVS2) operational",
        //   blueDescription: "Any poor performance, unexpected or unexplained event",
        //   yellowDescription: "Failure of either side of the 480V SWB (LVS1 or LVS2)",
        //   redDescription: "Failure to recover 480Vac ESB within 15 minutes or less",
        //   type: "formula",
        // },
        // {
        //   itemCategory: "Diesel Generators",
        //   itemName: "Diesel Generators",
        //   greenDescription:
        //     "All Diesel Generators Online                              Max 45% (1530kW) per Bus in Split Bus Configuration",
        //   blueDescription:
        //     "Any alarm, poor performance, unexpected or unexplained event or warning on any Generator",
        //   yellowDescription: "Loss of a Diesel Generator",
        //   redDescription: "",
        //   type: "formula",
        // },
        {
          itemCategory: "24Vdc & UPS's",
          id: "Aux UPS supply",
          itemName: "Aux UPS supply",
          greenDescription: "No UPS in bypass. No UPS alarms and batteries fully charged.",
          blueDescription: "Any UPS in bypass or in alarm",
          yellowDescription:
            "Main supply cannot be reinstated, UPS on batteries or total UPS failure",
          redDescription: "",
          type: "checkbox",
          checklist: auxUPSChecklist,
        },
        {
          itemCategory: "24Vdc & UPS's",
          id: "Thruster and Generator 24Vdc Supplies",
          itemName: "Thruster and Generator 24Vdc Supplies",
          greenDescription: "Batteries fully charged and charger supply available",
          blueDescription: "Any charger alarm",
          yellowDescription: "Any system on batteries",
          redDescription: "",
          type: "checkbox",
          checklist: thrusterGen24VdcChecklist,
        },
        {
          itemCategory: "HVAC",
          id: "DP Critical Ventilation / Fans",
          itemName: "DP Critical Ventilation / Fans",
          greenDescription: "All supply and exhaust fans running in critical spaces",
          blueDescription: "Any reduced ventilation",
          yellowDescription: "Complete loss of ventilation resulting in power reduction",
          redDescription: "",
          type: "checkbox",
          checklist: dpVentilationChecklist,
        },
        {
          itemCategory: "HVAC",
          id: "Air Conditioning",
          itemName: "Air Conditioning",
          greenDescription: "Air conditioning / cooling fully functional in all DP critical areas",
          blueDescription: "Any reduced air conditioning/cooling",
          yellowDescription: "Air conditioning or chiller stopped",
          redDescription: "",
          type: "checkbox",
          checklist: airConditioningChecklist,
        },
        {
          itemCategory: "Start / Control Air",
          id: "Compressed Air",
          itemName: "Compressed Air",
          greenDescription: "Operating normally with no alarms",
          blueDescription: "Any alarm present",
          yellowDescription: "any loss of start or control air",
          redDescription: "",
          type: "checkbox",
          checklist: compressedAirChecklist,
        },
        {
          itemCategory: "Comms",
          id: "Loss or Problem with any Essential Communications-(Bridge / Eng Rm / DECK / Surface  Facility)",
          itemName:
            "Loss or Problem with any Essential Communications-(Bridge / Eng Rm / DECK / Surface  Facility)",
          greenDescription: "Redundant communications",
          blueDescription: "Any problems with communications",
          yellowDescription: "One comms system remaining",
          redDescription: "",
          type: "checkbox",
          checklist: essentialCommsChecklist,
        },
        // {
        //   itemCategory: "DP Control System",
        //   itemName: "DP Controllers & Power Supplies",
        //   greenDescription: "Both controllers and power supplies available",
        //   blueDescription: "no alarms",
        //   yellowDescription: "Any alarms or poor performance or back up not available",
        //   redDescription: "Only one controller or one power supply operating",
        //   type: "",
        //   greenCondition: "checkbox",
        //   blueCondition: "",
        //   yellowCondition: "",
        //   redCondition: "",
        // },
        {
          itemCategory: "DP Control System",
          id: "Consequence Analysis",
          itemName: "Consequence Analysis",
          greenDescription: "DP2 Is activated",
          blueDescription: "Any active warning",
          yellowDescription: "",
          redDescription: "",
          type: "checkbox",
          checklist: dpConsequenceAnalysisChecklist,
        },
        // {
        //   itemCategory: "DP Control System",
        //   itemName: "DP Control System (CCs) See Title Block for DP Computer Reboot Policy",
        //   greenDescription: "Three controller computers available",
        //   blueDescription: "no alarms",
        //   yellowDescription: "Any alarms or poor performance or loss of any CC",
        //   redDescription: "Only one CC or one power supply operating",
        //   type: "",
        // },
        {
          itemCategory: "DP Control System",
          id: "DP Thruster Controllers",
          itemName: "DP Thruster Controllers",
          greenDescription: "All controllers available",
          blueDescription: "no alarms",
          yellowDescription: "Any problem with a controller any alarm",
          redDescription: "Loss of  one or more controller",
          type: "checkbox",
          checklist: dpThrusterControllersChecklist,
        },
        {
          itemCategory: "DP Control System",
          id: "DP Workstations See Title Block for DP Computer Reboot Policy",
          itemName: "DP Workstations See Title Block for DP Computer Reboot Policy",
          greenDescription: "All operating normally, no alarms",
          blueDescription: "Any alarm, poor performance, unexpected or unexplained event",
          yellowDescription: "One operator station remaining",
          redDescription: "",
          type: "checkbox",
          checklist: dpWorkstationsChecklist,
        },
        {
          itemCategory: "Networks",
          id: "DP  Network",
          itemName: "DP  Network",
          greenDescription: "Dual",
          blueDescription: "Any alarms or poor performance",
          yellowDescription: "1 network lost",
          redDescription: "Complete loss of networks",
          type: "checkbox",
          checklist: dpNetworkChecklist,
        },
        {
          id: "rule-4",
          itemCategory: "Sensors",
          itemName: "Heading Sensors (Gyro)",
          greenDescription: "3 Gyros available",
          blueDescription: "Any gyro in alarm, loss of 1 gyro",
          yellowDescription: "1 remaining gyro",
          redDescription: "",
          type: "formula",
          datapoints: [
            {
              info: {
                cat: "Gyro 1",
                seq: 152,
                tag: null,
                unit: null,
                label: "Status",
                dataSystemName: "DPS",
                metricId: 798,
              },
              alias: "sensor1",
            },
            {
              info: {
                cat: "Gyro 2",
                seq: 153,
                tag: null,
                unit: null,
                label: "Status",
                dataSystemName: "DPS",
                metricId: 799,
              },
              alias: "sensor2",
            },
            {
              info: {
                cat: "Gyro 3",
                seq: 154,
                tag: null,
                unit: null,
                label: "Status",
                dataSystemName: "DPS",
                metricId: 800,
              },
              alias: "sensor3",
            },
          ],
        },
        {
          id: "rule-5",
          itemCategory: "Sensors",
          itemName: "Motion sensors (MRU/VRU) into DP",
          greenDescription: "3 VRUs available",
          blueDescription: "Loss of up to 2 VRUs or alarm",
          yellowDescription: "Loss of all VRUs",
          redDescription: "",
          type: "formula",
          datapoints: [
            {
              info: {
                cat: "VRU 1",
                seq: 177,
                tag: null,
                unit: null,
                label: "Status",
                dataSystemName: "DPS",
                metricId: 823,
              },
              alias: "sensor1",
            },
            {
              info: {
                cat: "VRU 2",
                seq: 178,
                tag: null,
                unit: null,
                label: "Status",
                dataSystemName: "DPS",
                metricId: 824,
              },
              alias: "sensor2",
            },
            {
              info: {
                cat: "VRU 3",
                seq: 179,
                tag: null,
                unit: null,
                label: "Status",
                dataSystemName: "DPS",
                metricId: 825,
              },
              alias: "sensor3",
            },
          ],
        },
        {
          id: "rule-6",
          itemCategory: "Sensors",
          itemName: "Wind Sensors",
          greenDescription: "3 Wind sensors available",
          blueDescription: "Loss of up to 2 wind sensors or alarm",
          yellowDescription: "Loss of all wind sensors and gusty conditions",
          redDescription: "",
          type: "formula",
          datapoints: [
            {
              info: {
                cat: "Wind and Current",
                seq: 192,
                tag: null,
                unit: null,
                label: "Wind 1 Status",
                dataSystemName: "DPS",
                metricId: 838,
              },
              alias: "sensor1",
            },
            {
              info: {
                cat: "Wind and Current",
                seq: 193,
                tag: null,
                unit: null,
                label: "Wind 2 Status",
                dataSystemName: "DPS",
                metricId: 839,
              },
              alias: "sensor2",
            },
            {
              info: {
                cat: "Wind and Current",
                seq: 194,
                tag: null,
                unit: null,
                label: "Wind 3 Status",
                dataSystemName: "DPS",
                metricId: 840,
              },
              alias: "sensor3",
            },
          ],
        },
        {
          itemCategory: "Position Reference Systems",
          id: "DP Position Reference Systems-(as per Title Block = 50m from a surface facility)",
          itemName:
            "DP Position Reference Systems-(as per Title Block = 50m from a surface facility)",
          greenDescription:
            "The required position reference system confuguration is SceneScan and RangeGuard with the most reliable DGNSS enabled. Note: with both DGNSS enabled, the potential for voting error is increased.",
          blueDescription:
            "Any alarm or poor performance.  Loss of 1 Relative reference system.  Risk assessment and email notification required.",
          yellowDescription: "Less than three position reference systems available",
          redDescription: "",
          type: "checkbox",
          checklist: dpPositionReferenceChecklist,
        },
        {
          itemCategory: "Fire / Flood / Collision",
          id: "Fire - E/R or elsewhere",
          itemName: "Fire - E/R or elsewhere",
          greenDescription: "No fire, no fire alarms",
          blueDescription: "Any fire alarms",
          yellowDescription: "Fire confirmed",
          redDescription: "",
          type: "checkbox",
          checklist: fireChecklist,
        },
        {
          itemCategory: "Fire / Flood / Collision",
          id: "Flood",
          itemName: "Flood",
          greenDescription: "No bilge alarms active, no flooding",
          blueDescription: "Any bilge alarms",
          yellowDescription: "Flood confirmed",
          redDescription: "",
          type: "checkbox",
          checklist: floodChecklist,
        },
        {
          itemCategory: "Fire / Flood / Collision",
          id: "Collision (errant vessels)",
          itemName: "Collision (errant vessels)",
          greenDescription: "No collision imminent / minimum approach 150m",
          blueDescription: "Minimum approach will be < 150m",
          yellowDescription: "If collision possible",
          redDescription: "Collision imminent",
          type: "checkbox",
          checklist: collisionChecklist,
        },
      ]
    );
  }

  async getDataSystemList() {
    const request = axios.post<IDataSystem[]>(`datasystem-list`);
    return request;
  }

  async getAlarmVessels() {
    return axios.get<Record<string, IVesselRowData>>("ea/l1");
  }

  async getVesselAlarmList(vesselId: string) {
    return axios.post<IVesselAlarmSystemListAPIResponse>("ea/l2", {
      dbId: vesselId,
    });
  }

  async getVesselAlarmSystemList(vesselId: string, dpId: number) {
    return axios.post<Record<string, IAlarmSystemData[]>>("ea/l3", {
      dbId: vesselId,
      dPoints: [dpId],
    });
  }

  async getAlarmHistory(vesselId: string, dpId: number) {
    return axios.post<IAlarmHistoryAPIResponse>("ea/l4", {
      dbId: vesselId,
      dPoints: [dpId],
    });
  }

  async getGroupsList(vesselIds: string[], dataSystemIds: number[]) {
    const request = axios.post<APIDataGroup[]>(`datapoint-group-list`, {
      vesselId: vesselIds,
      dataSystemId: dataSystemIds,
    });
    return request;
  }

  async getDataPoints(vesselIds: string[], filters: IFilter[]) {
    const request = axios.post<APIDataPoint[]>(`datapoint-list`, [
      {
        vesselId: vesselIds,
        filter: filters,
      },
    ]);
    return request;
  }

  async getKeyEventDetails(bridgeLockId: number) {
    const request = axios.post(`key-event-details`, {
      bridgeLockId,
    });
    return request;
  }

  async alarmThresholds(params: IAlarmRequestParams) {
    const request = axios.post(`get-alarm-threshold`, params);
    return request;
  }

  async getUserDetails() {
    const request = axios.post(`user-details`, {
      req: "read",
    });
    return request;
  }

  async getReports({ vesselId, reportId }: { vesselId?: string; reportId?: number }) {
    const request = axios.post<IReportData[]>(`report`, {
      req: "read",
      vesselId,
      reportId,
    });
    return request;
  }

  async createReport(data: IReportRequestBody) {
    const request = axios.post<IReportData>(`report`, {
      req: "write",
      meta: {
        //   reportStatus: data.status,
        vesselId: data.vesselId,
      },
      comments: [],
      data,
    });
    return request;
  }
  async editReport(data: IReportRequestBody, reportId: number) {
    const { comments, ...rest } = data;
    const request = axios.post<IReportData>(`report`, {
      req: "update",
      data: {
        ...rest,
      },
      meta: {
        reportId,
      },
      comments,
    });
    return request;
  }

  async deleteReport(reportId: number) {
    const request = axios.post<{ report: IReportData }>(`report`, {
      req: "delete",
      meta: {
        reportId,
      },
    });
    return request;
  }

  async getTrendChartNotes(params: INoteFetchParams) {
    const request = axios.post<INoteResponseData>(`data-note`, {
      req: "read",
      ...params,
    });
    return request;
  }

  async createTrendChartNotes(params: INoteCreateParams) {
    const request = axios.post(`data-note`, {
      req: "write",
      returnDpNotes: true,
      ...params,
    });
    return request;
  }

  async editTrendChartNotes(params: INoteCreateParams, noteId: number) {
    const request = axios.post(`data-note`, {
      req: "write",
      returnDpNotes: true,
      noteId,
      ...params,
    });
    return request;
  }

  async updateUserSettings(params: IUserSettingsUpdateRequestParams) {
    const request = axios.post(`user-details`, {
      req: "update",
      userSettings: {
        ...params,
      },
    });
    return request;
  }

  async getEventLog(data: IEventHistoryRequestBody[]) {
    const request = axios.post<IEventLogResponse>(`event-log`, data);
    return request;
  }

  async getAlarmData(vesselId: string, alarmId?: number[]) {
    const requestData: { vesselId: string; alarmId?: number[] } = {
      vesselId,
    };

    if (alarmId !== undefined && alarmId !== null) {
      requestData.alarmId = alarmId;
    }

    const request = axios.post<IAlarmDataResponse>(`alarm-triggered`, requestData);
    return request;
  }

  async getSystemDataCount(config: {
    vesselId: string;
    from: Date;
    to: Date;
    interval?: number;
    dataSystemIds: string[];
  }) {
    const timeInterval = Math.floor((config.to.getTime() - config.from.getTime()) / 1000) / 100;

    const request = axios.post<Record<string, ISystemDataCountResponse[]>>(`system-status`, {
      apiFunction: "db_data_count",
      req: "read",
      timeFrom: config.from,
      timeTo: config.to,
      timeInterval,
      vesselId: config.vesselId,
      dataSystemName: config.dataSystemIds.map((id) => id.substring(3)),
    });
    return request;
  }

  async getDatapointDescription(dPoints: number[]) {
    const request = axios.post<Record<"dpDesc", Record<string, APIDataPointDesc>>>(
      `datapoint-description`,
      {
        dPoints,
      }
    );
    return request;
  }

  async getVesselStateEvent(vesselId: string, commandId: number) {
    const request = axios.post<{ data: IVesselState }>("vessel-ea-control", {
      req: "read",
      data: {
        meta: {
          vesselId,
          commandId,
        },
      },
    });
    return request;
  }

  async setVesselState(data: IVesselStateRequestBody) {
    const request = axios.post<{ data: IVesselState }>("vessel-ea-control", {
      req: "write",
      ...data,
    });
    return request;
  }

  async getVesselConfigs(vesselId: string, dPoints?: number[]) {
    const request = axios.post(`vessel-config`, {
      vesselId,
      dPoints,
    });
    return request.then((response) => ({
      ...response,
      data: response.data[vesselId],
    }));
  }

  async getAlarmAck(vesselId: string, ackId: number) {
    const request = axios.post<{ data: IAckResponseData }>(`alarm-ack`, {
      req: "read",
      data: {
        meta: {
          vesselId,
          ackId,
        },
      },
    });
    return request;
  }

  async ackAlarm(authorName: string, data: IAckRequestData) {
    const request = axios.post<{ data: IAckResponseData }>(`alarm-ack`, {
      req: "write",
      authorName,
      data,
    });
    return request;
  }

  // Get alarm conditions
  async getAlarmConditions() {
    const request = axios.get<{ data: IAlarmCondition[]; locks: LockValue[] }>(`alarm-conditions`);
    return request;
  }

  // Save alarm conditions
  async saveAlarmConditions(conditions: IAlarmCondition[]) {
    const request = axios.post(`alarm-conditions`, {
      data: conditions,
    });
    return request;
  }

  async deleteAlarmCondition(conditionId: string) {
    const request = axios.delete(`alarm-conditions/${conditionId}`);
    return request;
  }

  async lockAlarmCondition(conditionId: string) {
    const request = axios.put(`alarm-conditions/${conditionId}/lock`);
    return request;
  }

  async unlockAlarmCondition(conditionId: string) {
    const request = axios.delete(`alarm-conditions/${conditionId}/lock`);
    return request;
  }

  async setFollowedAlarms(vesselId: string, dPoints: number[]) {
    const request = axios.post(`alarm-follow`, {
      req: "write",
      dPoints,
      vesselId,
    });
    return request;
  }

  async getHelmReport() {
    const request = axios.get(`helmconnect`);
    return request;
  }

  async setCardsToDashboard(
    vesselId: string,
    configId: number,
    typeId: number,
    cards: IDashboardCard[],
    nodes: IChartElement[],
    connections: INodeConnection[],
    title?: string,
    backgroundImage?: string
  ) {
    const request = axios.post<IDashboardCardResponse>(`dashboard-config`, {
      req: title ? "write" : "update",
      vesselId,
      configId,
      typeId,
      data: {
        config: {
          title,
          backgroundImage,
          cards: [...(cards ?? [])],
          nodes: [...(nodes ?? [])],
          connections: [...(connections ?? [])],
        },
      },
    });
    return request;
  }

  async updateDashboardConfig(
    vesselId: string,
    configId: number,
    config: {
      title: string;
      cards: IDashboardCard[];
      nodes: IChartElement[];
      connections: INodeConnection[];
      typeId: number;
      isPublic?: boolean;
    }
  ) {
    const request = axios.post(`dashboard-config`, {
      req: "update",
      vesselId,
      configId,
      typeId: config.typeId,
      isPublic: config.isPublic,
      data: {
        config: {
          title: config.title,
          cards: [...(config.cards ?? [])],
          nodes: [...(config.nodes ?? [])],
          connections: [...(config.connections ?? [])],
        },
      },
    });

    return request;
  }

  async deleteDashboardConfig(vesselId: string, configId: number) {
    const request = axios.post(`dashboard-config`, {
      req: "delete",
      vesselId,
      configId,
    });

    return request;
  }

  async getDashboardConfigsList({ vesselId, typeId }: { vesselId?: string; typeId?: number }) {
    const request = axios.post<Array<IDashboardConfigListItem>>(`dashboard-config`, {
      req: "read",
      vesselId,
      typeId,
    });
    return request;
  }

  async getDashboardConfig(vesselId: string, configId: number) {
    const request = axios.post<IDashboardCardResponse>(`dashboard-config`, {
      req: "read",
      vesselId,
      configId,
    });
    return request;
  }

  async moveDhubToDashboard(vesselId: string, configId: number, config: any) {
    const request = axios.post(`dashboard-config`, {
      req: "write",
      vesselId,
      configId,
      typeId: 2,
      data: {
        config,
      },
    });
    return request;
  }

  async getStreamConfig() {
    const request = axios.get<IStreamConfig>(`stream`);
    return request;
  }

  // Access Profile endpoints
  async getModulesList() {
    const request = axios<{ modules: ISystemModule[] }>({
      method: "post",
      url: "access-management",
      // baseURL: "https://c-connect.mtllc.us:8080/v1/",
      data: {
        apiFunction: "module",
      },
    });

    return request;
  }

  // Access Profile endpoints
  async getFunctionsList() {
    const request = axios<{ functions: ISystemFunction[] }>({
      method: "post",
      url: "access-management",
      // baseURL: "https://c-connect.mtllc.us:8080/v1/",
      data: {
        apiFunction: "function",
      },
    });

    return request;
  }

  // Access
  async getCompanyAccessList(companyId: number) {
    const request = axios<ICompanyAccess[]>({
      method: "post",
      url: "access-management",
      // baseURL: "https://c-connect.mtllc.us:8080/v1/",
      data: {
        apiFunction: "company-access",
        companyId,
        req: "read",
      },
    });

    return request;
  }

  createCompanyAccess(config: {
    vesselId: string;
    companyId: number;
    moduleIdAccess: number[];
    datapointAccess: number[];
  }) {
    const request = axios({
      method: "post",
      url: "access-management",
      // baseURL: "https://c-connect.mtllc.us:8080/v1/",
      data: {
        apiFunction: "company-access",
        req: "write",
        ...config,
      },
    });

    return request;
  }

  updateCompanyAccess(config: {
    companyAccessId: number;
    companyId?: number;
    moduleIdAccess?: number[];
    datapointAccess?: number[];
  }) {
    const request = axios<ICompanyAccess[]>({
      method: "post",
      url: "access-management",
      // baseURL: "https://c-connect.mtllc.us:8080/v1/",
      data: {
        apiFunction: "company-access",
        req: "update",
        ...config,
      },
    });

    return request;
  }

  deleteCompanyAccess(companyAccessId: number) {
    const request = axios<ICompanyAccess[]>({
      method: "post",
      url: "access-management",
      // baseURL: "https://c-connect.mtllc.us:8080/v1/",
      data: {
        apiFunction: "company-access",
        companyAccessId,
        req: "delete",
      },
    });

    return request;
  }

  async getSourceDataInfo(filter: Array<{ dbId: string; dPoints: number[] }>) {
    const request = axios.post<Record<string, Pick<IGetDataVesselData, "dataPointInfo">>>(
      `get-data`,
      {
        apiFunction: "sourceDescription",
        req: "read",
        filter,
      }
    );
    return request;
  }

  async getCompanyList() {
    const request = axios<ICompany[]>({
      method: "post",
      url: "access-management",
      // baseURL: "https://c-connect.mtllc.us:8080/v1/",
      data: {
        apiFunction: "company",
        req: "read",
      },
    });

    return request;
  }

  async updateCompany(data: Partial<ICompany>) {
    const request = axios<ICompany[]>({
      method: "post",
      url: "access-management",
      data: {
        apiFunction: "company",
        req: "update",
        ...data,
      },
    });

    return request;
  }

  async getAccessProfileList() {
    const request = axios<ICompanyProfile[]>({
      method: "post",
      url: "access-management",
      // baseURL: "https://c-connect.mtllc.us:8080/v1/",
      data: {
        apiFunction: "profile",
        req: "read",
      },
    });

    return request;
  }

  async createAccessProfile(data: {
    profileName: string;
    functionIdRead: number[];
    functionIdWrite: number[];
    functionIdUpdate: number[];
    functionIdDelete: number[];
  }) {
    const request = axios<ICompanyProfile[]>({
      method: "post",
      url: "access-management",
      // baseURL: "https://c-connect.mtllc.us:8080/v1/",
      data: {
        apiFunction: "profile",
        req: "write",
        ...data,
      },
    });

    return request;
  }

  async updateAccessProfile(data: {
    profileId: number;
    functionIdRead: number[];
    functionIdWrite: number[];
    functionIdUpdate: number[];
    functionIdDelete: number[];
  }) {
    const request = axios<ICompanyProfile[]>({
      method: "post",
      url: "access-management",
      // baseURL: "https://c-connect.mtllc.us:8080/v1/",
      data: {
        apiFunction: "profile",
        req: "update",
        ...data,
      },
    });

    return request;
  }

  async deleteAccessProfile(profileId: number) {
    const request = axios<ICompanyProfile[]>({
      method: "post",
      url: "access-management",
      // baseURL: "https://c-connect.mtllc.us:8080/v1/",
      data: {
        apiFunction: "profile",
        req: "delete",
        profileId,
      },
    });

    return request;
  }

  async getCompanyUsers(filters: Record<string, number>) {
    const request = axios<ICompanyUser[]>({
      method: "post",
      url: "access-management",
      // baseURL: "https://c-connect.mtllc.us:8080/v1/",
      data: {
        apiFunction: "company-user-role",
        req: "read",
        filter: {
          ...filters,
        },
      },
    });

    return request;
  }

  async companyUserRoleBulk(
    data: Array<{
      companyId: number;
      userId: number;
      profileId: number;
    }>
  ) {
    const request = axios<ICompanyUser[][]>({
      method: "post",
      url: "access-management",
      // baseURL: "https://c-connect.mtllc.us:8080/v1/",
      data: data.map((d) => ({
        apiFunction: "company-user-role",
        req: "update",
        ...d,
      })),
    });

    return request;
  }

  async createCompanyUserRole(data: { companyId: number; userId: number; profileId: number }) {
    const request = axios<ICompanyUser[]>({
      method: "post",
      url: "access-management",
      // baseURL: "https://c-connect.mtllc.us:8080/v1/",
      data: {
        apiFunction: "company-user-role",
        req: "write",
        ...data,
      },
    });

    return request;
  }

  async editCompanyUserRole(data: { companyUserId: number; profileId: number }) {
    const request = axios<ICompanyUser[]>({
      method: "post",
      url: "access-management",
      // baseURL: "https://c-connect.mtllc.us:8080/v1/",
      data: {
        apiFunction: "company-user-role",
        req: "update",
        ...data,
      },
    });

    return request;
  }

  async deleteCompanyUserRole(companyUserId: number) {
    const request = axios<ICompanyUser[]>({
      method: "post",
      url: "access-management",
      // baseURL: "https://c-connect.mtllc.us:8080/v1/",
      data: {
        apiFunction: "company-user-role",
        req: "delete",
        companyUserId,
      },
    });

    return request;
  }

  async getUserAccess(filters: { companyId?: number[]; userId?: number; vesselId?: string[] }) {
    const request = axios<IUserAccess[]>({
      method: "post",
      url: "access-management",
      // baseURL: "https://c-connect.mtllc.us:8080/v1/",
      data: {
        apiFunction: "user-access",
        req: "read",
        ...filters,
      },
    });

    return request;
  }

  // Reset user password
  async resetPassword(email: string) {
    const request = axios({
      method: "post",
      url: "password/" + email,
    });

    return request;
  }

  async userAccessBulk(data: IUserAccessRequestData[]) {
    const request = axios<IUserAccess[][]>({
      method: "post",
      url: "access-management",
      data: data.map((d) => ({
        apiFunction: "user-access",
        ...d,
      })),
    });

    return request;
  }

  async companyAccessBulk(data: ICompanyAccessRequestData[]) {
    const request = axios<ICompanyAccess[][]>({
      method: "post",
      url: "access-management",
      data: data.map((d) => ({
        apiFunction: "company-access",
        ...d,
      })),
    });

    return request;
  }

  async createUser(data: {
    firstName: string;
    lastName: string;
    email: string;
    companyId: number;
    accessProfileId: number;
    position: string;
  }) {
    const request = axios({
      method: "post",
      url: "create-user",
      data,
    });

    return request;
  }

  async getVesselDataPoints(vesselIds: string[]) {
    const request = axios<IVesselDatapointsListItem[]>({
      method: "post",
      url: "access-management",
      data: {
        apiFunction: "vessel-datapoint-list",
        vesselId: vesselIds,
      },
    });

    return request;
  }

  async getCobrasMapData(filter: Record<string, any>) {
    const request = axios<Record<string, Record<string, ICobrasJobResult[]>>>({
      method: "post",
      url: "cobras",
      data: {
        req: "read",
        apiFunction: "cobrasResult",
        filter,
        // vesselId: vesselIds,
      },
    });

    return request;
  }

  async getCobrasJobs() {
    const request = axios<ICobrasJob[]>({
      method: "post",
      url: "cobras",
      data: {
        req: "read",
        apiFunction: "cobrasJob",
        filter: {
          statusId: [
            ECobrasJobStatus.Created,
            ECobrasJobStatus.Queued,
            ECobrasJobStatus.Processing,
            ECobrasJobStatus.Failed,
            ECobrasJobStatus.Abort,
            ECobrasJobStatus.Complete,
          ],
        },
      },
    });

    return request;
  }

  async createCobrasJob(data: ICobrasJobConfig) {
    const { dataPointC, dataPointsX, dataPointsY, vesselId } = data;
    const request = axios<ICobrasJob>({
      method: "post",
      url: "cobras",
      data: {
        req: "write",
        apiFunction: "cobrasJob",
        data: { dataPointC, dataPointsX, dataPointsY },
        meta: {
          vesselId,
        },
      },
    });

    return request;
  }

  async updateCobrasJobStatus(jobId: string, statusId: ECobrasJobStatus) {
    const request = axios<ICobrasJob>({
      method: "post",
      url: "cobras",
      data: {
        req: "update",
        apiFunction: "cobrasJob",
        meta: {
          jobId,
          statusId,
        },
      },
    });

    return request;
  }

  // Files endpoints
  async uploadFile(file: File) {
    const formData = new FormData();
    formData.append("files", file);

    const request = axios<{
      failed: null | string[];
      success: null | Record<string, string>;
    }>({
      method: "post",
      url: "file",
      data: formData,
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return request;
  }

  async fetchFile(filename: string) {
    const request = axios({
      method: "get",
      url: "file",
      params: {
        filename,
      },
    });

    return request;
  }

  // TODO update with real data
  async getCobrasData(vesselId: string, signalId: number, dataPointIds: number[]) {
    return {
      result: [
        [
          0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0,
          0.0, 0.0, 0.0, 0.0,
        ],
        [
          0.0, 0.0, 0.0, 0.0, 0.0, 0.9951684150100166, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0,
          0.0, 0.0, 0.0, 0.0, 0.0, 0.06713769771376303, 0.0,
        ],
        [
          0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0,
          0.0, 0.0, 0.0, 0.0,
        ],
        [
          0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0,
          0.0, 0.0, 0.0, 0.0,
        ],
        [
          0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0,
          0.0, 0.0, 0.0, 0.0,
        ],
        [
          0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0,
          0.0, 0.0, 0.041672889707143154, 0.0,
        ],
        [
          0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0,
          0.0, 0.0, 0.0, 0.0,
        ],
        [
          0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0,
          0.0, 0.0, 0.0, 0.0,
        ],
        [
          0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0,
          0.0, 0.0, 0.0, 0.0,
        ],
        [
          0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0,
          0.0, 0.0, 0.0, 0.0,
        ],
        [
          0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0,
          0.0, 0.0, 0.0, 0.0,
        ],
        [
          0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0,
          0.0, 0.0, 0.0, 0.0,
        ],
        [
          0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0,
          0.0, 0.0, 0.0, 0.0,
        ],
        [
          0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0,
          0.0, 0.0, 0.0, 0.0,
        ],
        [
          0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0,
          0.0, 0.0, 0.0, 0.0,
        ],
        [
          0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0,
          0.0, 0.0, 0.0, 0.0,
        ],
        [
          0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0,
          0.0, 0.0, 0.0, 0.0,
        ],
        [
          0.0, -0.09301888146172743, 0.0, 0.0, 0.0, -0.12643411019239414, 0.0, 0.0, 0.0, 0.0, 0.0,
          0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.7213879090313896, 0.9561802102453812,
          0.6837580952027157, 0.8787498406314941,
        ],
        [
          0.0, -0.3311006054073475, 0.0, 0.0, 0.0, -0.3867114182335393, 0.0, 0.0, 0.0, 0.0, 0.0,
          0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.7035754371546971, 0.5342130124232628,
          0.6413005782501767,
        ],
        [
          0.0, -0.04618818764529126, 0.0, 0.0, 0.0, -0.07322230428419814, 0.0, 0.0, 0.0, 0.0, 0.0,
          0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.7187447381937556, 0.8833468112779983,
        ],
        [
          0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0,
          0.0, 0.0, 0.0, 0.5403501715833604,
        ],
        [
          0.0, -0.13949497773947384, 0.0, 0.0, 0.0, -0.17597961439959056, 0.0, 0.0, 0.0, 0.0, 0.0,
          0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0,
        ],
      ],
    };
  }

  // Backoffice
  async getVesselDataConfig(vesselId: string) {
    const request = axios<IBackofficeDataPointConfig[]>({
      method: "post",
      url: "boa",
      data: {
        req: "read",
        apiFunction: "vesselConfig",
        filter: {
          vesselId,
        },
      },
    });

    return request;
  }

  async updateVesselDataConfig(data: IBackofficeDataPointConfig[]) {
    const request = axios<{
      updated: IBackofficeDataPointConfig[];
      noUpdateChecksum: IBackofficeDataPointConfig[];
    }>({
      method: "post",
      url: "boa",
      data: {
        req: "update",
        apiFunction: "vesselConfig",
        data,
      },
    });

    return request;
  }

  async getVesselInfo(vesselId: string) {
    const request = axios<IVesselInfoPageData>({
      method: "post",
      url: "boa",
      data: {
        req: "read",
        apiFunction: "vesselProperties",
        vesselId,
      },
    });

    return request;
  }

  async updateVesselInfo(data: IVesselInfoPageData) {
    const request = axios<IVesselInfoPageData>({
      method: "post",
      url: "boa",
      data: {
        req: "update",
        apiFunction: "vesselProperties",
        vesselId: data.meta.vesselId,
        data: data.data,
      },
    });

    return request;
  }

  async getAccessRequest(vesselId: string) {
    const request = axios.get<IKeyStatusResponse>(`keygen?vessel_id=${vesselId}`);
    return request;
  }

  async closeKey(vesselId: string, closeMessage: string) {
    const request = axios.post<IKeyGenResponse>(
      `closekey`,
      JSON.stringify({
        vessel_id: vesselId,
        close_message: closeMessage,
      })
    );
    return request;
  }

  async getAccessCode(
    vesselId: string,
    duration: number,
    encryption: string,
    clientTime: Spacetime,
    reason: string
  ) {
    const request = axios.post<IKeyGenResponse>(
      `keygen`,
      JSON.stringify({
        vessel_id: vesselId,
        duration: duration,
        enc_code: encryption,
        client_time: `${clientTime.unixFmt("yyyy-MM-dd")}T${clientTime.unixFmt("HH:mm:ss")}Z`,
        access_reason: reason,
      })
    );
    return request;
  }
}

interface QueryConfig {
  /**
   * The policy to use when executing the query.
   * EQueryFlow.Race: Race conditions are allowed
   * EQueryFlow.Latest: Only the latest call will be resolved
   * EQueryFlow.InOrder: Calls will be resolved in order, and queued
   * @default EQueryFlow.Race
   */
  policy?: EQueryFlow;
  /**
   * If true, the data will be kept during refetch.
   * @default true
   */
  keepDuringRefetch?: boolean;
  /**
   * Debounce the query execution. In ms.
   * @default 0
   */
  debounce?: number;
}

const defaultConfig: QueryConfig = {
  policy: EQueryFlow.Race,
  keepDuringRefetch: true,
  debounce: 0,
};

/**
 * An object format which makes synchronous checks of a Promise easier.
 */
export class Query<T> {
  data: T | null;
  isLoading: boolean;
  isError: boolean;
  error: string | null;
  response: AxiosResponse<T> | null;
  statusCode: number | null;
  taskQueue: any[];
  latestCallId = 0;

  private asyncFunction: (...args: any[]) => Promise<AxiosResponse<T>>;
  private settings: QueryConfig;

  constructor(
    asyncFunction: (...args: any[]) => Promise<AxiosResponse<T>>,
    param: EQueryFlow | QueryConfig
  ) {
    this.data = null;
    this.isLoading = false;
    this.isError = false;
    this.error = null;
    this.response = null;
    this.statusCode = null;
    this.taskQueue = [];
    this.execute = this.execute.bind(this);

    if (param instanceof Object) {
      this.settings = { ...defaultConfig, ...param };
    } else {
      this.settings = { ...defaultConfig, policy: param };
    }
    // Add debouncing if exists
    if (this.settings.debounce > 0) this.execute = _.debounce(this.execute, this.settings.debounce);

    this.asyncFunction = asyncFunction;
  }

  get queueSize(): number {
    return this.taskQueue.length;
  }

  execute(...args: any[]): Promise<AxiosResponse<T>> {
    if (!this.settings.keepDuringRefetch) this.data = null;
    return this._execute(args, false);
  }

  private _execute(args: any[], isFromQueue: boolean): Promise<AxiosResponse<T>> {
    // If the policy is InOrder, and there is an ongoing request, queue args
    if (this.settings.policy === EQueryFlow.InOrder && this.isLoading && !isFromQueue) {
      this.taskQueue.push(args);
      return;
    }
    const currentCallId = ++this.latestCallId;
    this.isError = false;
    this.error = null;
    return new Promise<AxiosResponse<T>>((resolve, reject) => {
      this.isLoading = true;
      this.asyncFunction(...args)
        .then((response) => {
          if (this.settings.policy === EQueryFlow.Latest && currentCallId !== this.latestCallId) {
            return;
          }
          this.data = response.data;
          this.response = response;
          this.statusCode = response.status;
          this.error = null;
          this.isError = false;
          resolve(response);
        })
        .catch((error: AxiosError<T>) => {
          if (this.settings.policy === EQueryFlow.Latest && currentCallId !== this.latestCallId) {
            return;
          }
          this.error = errorToMessage(error);
          this.statusCode = error.response.status;
          this.response = error.response;
          this.isError = true;
          resolve(null);
        })
        .catch((error: TypeError) => {
          if (this.settings.policy === EQueryFlow.Latest && currentCallId !== this.latestCallId) {
            return;
          }
          this.error = error.message;
          this.isError = true;
          resolve(null);
        })
        .finally(() => {
          if (this.settings.policy === EQueryFlow.Latest && currentCallId !== this.latestCallId) {
            return;
          }
          if (this.settings.policy === EQueryFlow.InOrder && this.taskQueue.length > 0) {
            this._execute(this.taskQueue.shift(), true);
          }
          this.isLoading = false;
        });
    });
  }
}

/**
 * Returns the Query object for an async function.
 * @param asyncFunction Async function to get the Query of.
 * @returns A Query for the async function.
 */
export function querify<T>(
  asyncFunction: (...args: any[]) => Promise<AxiosResponse<T>>,
  policy?: EQueryFlow
): Query<T>;

export function querify<T>(
  asyncFunction: (...args: any[]) => Promise<AxiosResponse<T>>,
  config: QueryConfig
): Query<T>;

export function querify<T>(
  asyncFunction: (...args: any[]) => Promise<AxiosResponse<T>>,
  policyOrConfig: EQueryFlow | QueryConfig
): Query<T> {
  return new Query<T>(asyncFunction, policyOrConfig);
}

// TODO add translations
export const errorToMessage = (error: AxiosError) => {
  switch (error?.response?.status) {
    case 429:
      return `Too many failed login attempts. Try again after ${error.response.headers["retry-after"]} seconds.`;
  }
  return error.message;
};
